import React from 'react';
import { CheckIcon } from '@heroicons/react/24/solid';
import type { Plan } from './types';

interface PlanCardProps {
  plan: Plan;
  billingPeriod: 'monthly' | 'yearly';
  isCurrent: boolean;
  isUpgrading: boolean;
  isWelcomeFlow: boolean;
  checkoutDisabled?: boolean;
  buttonText: string;
  onUpgrade: (planId: string) => void;
}

export const PlanCard: React.FC<PlanCardProps> = ({
  plan,
  billingPeriod,
  isCurrent,
  isUpgrading,
  isWelcomeFlow,
  checkoutDisabled = false,
  buttonText,
  onUpgrade,
}) => {
  const monthlyPrice =
    billingPeriod === 'yearly'
      ? Math.round((plan.priceYearly || plan.priceMonthly * 10) / 12)
      : plan.priceMonthly;
  const isPro = plan.name === 'PROFESSIONAL';
  const isFree = plan.priceMonthly === 0 || plan.name?.toUpperCase() === 'FREE';

  return (
    <div
      className={`relative rounded-2xl shadow-xl w-full h-full ${
        isPro ? 'border-2 border-blue-600 scale-105' : 'border border-gray-200'
      }`}
    >
      {isPro && (
        <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
          <span className="inline-flex items-center px-4 py-1 rounded-full text-sm font-semibold bg-blue-600 text-white">
            Most Popular
          </span>
        </div>
      )}

      {isWelcomeFlow && isFree && (
        <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
          <span className="inline-flex items-center px-4 py-1 rounded-full text-sm font-semibold bg-green-600 text-white whitespace-nowrap">
            $0 today — card saved for future upgrades
          </span>
        </div>
      )}

      <div className="bg-white rounded-2xl p-8">
        <div className="text-center">
          <h3 className="text-2xl font-bold text-gray-900">{plan.displayName}</h3>
          <div className="mt-4 flex items-baseline justify-center">
            <span className="text-5xl font-extrabold text-gray-900">${monthlyPrice}</span>
            <span className="ml-1 text-xl font-semibold text-gray-500">/month</span>
          </div>
          {billingPeriod === 'yearly' && plan.priceMonthly > 0 && (
            <p className="mt-1 text-sm text-gray-500">
              Billed ${plan.priceYearly || plan.priceMonthly * 10} yearly
            </p>
          )}
        </div>

        <ul className="mt-8 space-y-4">
          {plan.features.map((feature, index) => (
            <li key={index} className="flex items-start">
              <CheckIcon className="flex-shrink-0 h-5 w-5 text-green-500" />
              <span className="ml-3 text-gray-700">{feature}</span>
            </li>
          ))}
        </ul>

        <div className="mt-8">
          <button
            onClick={() => (!isCurrent || isWelcomeFlow) && !checkoutDisabled && onUpgrade(plan.id)}
            disabled={isUpgrading || checkoutDisabled || (isCurrent && !isWelcomeFlow)}
            className={`w-full py-3 px-4 rounded-md font-semibold transition-colors ${
              isCurrent && !isWelcomeFlow
                ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                : isPro
                  ? 'text-white bg-blue-600 hover:bg-blue-700'
                  : 'text-gray-700 bg-gray-200 hover:bg-gray-300'
            }`}
          >
            {isUpgrading ? (
              <span className="flex items-center justify-center">
                <svg
                  className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  ></circle>
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  ></path>
                </svg>
                Processing...
              </span>
            ) : isCurrent && !isWelcomeFlow ? (
              'Your Current Plan'
            ) : (
              buttonText
            )}
          </button>
        </div>
      </div>
    </div>
  );
};
